import { useState, useRef, useEffect } from 'react';
import { XMarkIcon } from '@heroicons/react/20/solid';

/**
 * AutocompleteTagInput Component
 * Extends TagInput with predictive autocomplete from previously used tags.
 * Users can type to see suggestions, select from dropdown, or press Enter for free-text.
 */
function AutocompleteTagInput({
  tags = [],
  setTags,
  suggestions = [],
  placeholder = "Type and press Enter to add tags",
  maxTags = 10,
  className = ""
}) {
  const [inputValue, setInputValue] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  // Filter suggestions based on input, excluding already-selected tags
  const filteredSuggestions = inputValue.trim()
    ? suggestions.filter(
        (s) =>
          s.toLowerCase().includes(inputValue.trim().toLowerCase()) &&
          !tags.some((tag) => tag.toLowerCase() === s.toLowerCase())
      )
    : [];

  useEffect(() => {
    setHighlightedIndex(-1);
  }, [inputValue]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target) &&
        inputRef.current &&
        !inputRef.current.contains(e.target)
      ) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const addTag = (value) => {
    const trimmed = value.trim();
    if (!trimmed) return;
    const tagExists = tags.some((tag) => tag.toLowerCase() === trimmed.toLowerCase());
    if (!tagExists && tags.length < maxTags) {
      setTags([...tags, trimmed]);
    }
    setInputValue('');
    setShowSuggestions(false);
  };

  const handleInputChange = (e) => {
    setInputValue(e.target.value);
    setShowSuggestions(true);
  };

  const handleInputKeyDown = (e) => {
    if (e.key === 'ArrowDown' && filteredSuggestions.length > 0) {
      e.preventDefault();
      setHighlightedIndex((prev) =>
        prev < filteredSuggestions.length - 1 ? prev + 1 : 0
      );
    } else if (e.key === 'ArrowUp' && filteredSuggestions.length > 0) {
      e.preventDefault();
      setHighlightedIndex((prev) =>
        prev > 0 ? prev - 1 : filteredSuggestions.length - 1
      );
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (highlightedIndex >= 0 && filteredSuggestions[highlightedIndex]) {
        addTag(filteredSuggestions[highlightedIndex]);
      } else if (inputValue.trim()) {
        addTag(inputValue);
      }
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
    } else if (e.key === 'Backspace' && !inputValue && tags.length > 0) {
      setTags(tags.slice(0, -1));
    }
  };

  const removeTag = (indexToRemove) => {
    setTags(tags.filter((_, index) => index !== indexToRemove));
  };

  return (
    <div className="relative">
      <div
        className={`flex flex-wrap items-center gap-2 rounded-md shadow-sm ring-1 ring-inset ring-gray-200 focus-within:ring-2 focus-within:ring-inset focus-within:ring-indigo-600 px-3 py-2 min-h-[38px] ${className}`}
        onClick={() => inputRef.current?.focus()}
      >
        {tags.map((tag, index) => (
          <span
            key={index}
            className="inline-flex items-center gap-1 rounded-md bg-white px-2 py-1 text-xs font-medium text-gray-700 ring-1 ring-inset ring-gray-300"
          >
            {tag}
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                removeTag(index);
              }}
              className="group relative -mr-1 h-3.5 w-3.5 rounded-sm hover:bg-gray-200"
            >
              <span className="sr-only">Remove {tag}</span>
              <XMarkIcon className="h-3.5 w-3.5 text-gray-500 hover:text-gray-700" />
            </button>
          </span>
        ))}
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleInputKeyDown}
          onFocus={() => inputValue.trim() && setShowSuggestions(true)}
          placeholder={tags.length === 0 ? placeholder : ''}
          className="flex-1 min-w-[120px] border-0 bg-transparent py-0 px-0 text-gray-900 placeholder:text-gray-500 focus:ring-0 focus:outline-none sm:text-sm sm:leading-6"
          disabled={tags.length >= maxTags}
        />
      </div>
      {showSuggestions && filteredSuggestions.length > 0 && (
        <ul
          ref={dropdownRef}
          className="absolute z-10 mt-1 max-h-48 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black/5"
        >
          {filteredSuggestions.map((suggestion, index) => (
            <li
              key={suggestion}
              className={`cursor-pointer px-3 py-2 ${
                index === highlightedIndex
                  ? 'bg-indigo-600 text-white'
                  : 'text-gray-900 hover:bg-gray-100'
              }`}
              onMouseDown={(e) => {
                e.preventDefault();
                addTag(suggestion);
              }}
              onMouseEnter={() => setHighlightedIndex(index)}
            >
              {suggestion}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default AutocompleteTagInput;
